import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import PageSeoNote from "@/components/PageSeoNote";
import { istTodayISO, formatDMY } from "@/lib/date";
import { findKeywordConfig } from "@/constants/keywordLandings";

export default function PreviousResult() {
  const { pathname } = useLocation();
  const kw = findKeywordConfig(pathname);
  useSeo({
    title: kw?.title || "Morning Teer Previous Results | Shillong Result List",
    description:
      kw?.description ||
      "Browse Morning Teer Previous Results — filter historical Shillong Morning Teer FR and SR winning numbers by any date range.",
    path: pathname,
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Previous Results", path: pathname },
    ],
  });

  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);

  const load = () => {
    setLoading(true);
    const params = { limit: 5000 };
    if (start) params.start = start;
    if (end) params.end = end;
    api
      .get("/results", { params })
      .then((r) => setItems(r.data.items))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const today = istTodayISO();
  // Prepend today's row as "XX XX" if today isn't in items and no filter is set
  const showTodayPlaceholder = !start && !end && items.length > 0 && items[0].date !== today;
  const rows = showTodayPlaceholder
    ? [{ id: "today-placeholder", date: today, r1: null, r2: null, placeholder: true }, ...items]
    : items;

  return (
    <>
      <div className="max-w-xl mx-auto w-full px-4 pt-5 pb-2">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sky-700 text-sm font-semibold hover:text-sky-900">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <div className="text-center mt-4">
          <h1 className="font-heading font-black text-slate-900 text-3xl sm:text-4xl">
            Morning Teer Previous Result
          </h1>
        </div>
      </div>

      <div className="max-w-xl mx-auto w-full px-4 pb-6">
        {/* Filter (collapsible) */}
        <div className="flex justify-end mb-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFilterOpen(!filterOpen)}
            className="rounded-full text-xs"
            data-testid="pr-toggle-filter"
          >
            {filterOpen ? "Hide filter" : "Filter by date"}
          </Button>
        </div>
        {filterOpen && (
          <Card className="p-4 mb-4 border-slate-200">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-500">From</Label>
                <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1" data-testid="pr-start" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500">To</Label>
                <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1" data-testid="pr-end" />
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button onClick={load} className="flex-1" data-testid="pr-apply">
                Apply
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setStart("");
                  setEnd("");
                  setTimeout(load, 0);
                }}
                data-testid="pr-reset"
              >
                Reset
              </Button>
            </div>
          </Card>
        )}

        {/* Results table matching reference */}
        {loading ? (
          <div className="text-center text-slate-500 py-8">Loading…</div>
        ) : rows.length === 0 ? (
          <Card className="p-8 text-center border-dashed">
            <div className="text-slate-500 text-sm">No results in this range.</div>
          </Card>
        ) : (
          <div className="rounded-lg overflow-hidden ring-1 ring-slate-400 bg-white" data-testid="pr-list">
            <div className="grid grid-cols-[1.3fr_1.4fr_0.8fr_0.8fr] bg-[#5cc9a7] text-slate-900 text-center font-heading font-bold text-base">
              <div className="py-2.5 border-r border-slate-300">City</div>
              <div className="py-2.5 border-r border-slate-300">Date</div>
              <div className="py-2.5 border-r border-slate-300">F/R</div>
              <div className="py-2.5">S/R</div>
            </div>
            {rows.map((it) => (
              <div
                key={it.id}
                className="grid grid-cols-[1.3fr_1.4fr_0.8fr_0.8fr] border-t border-slate-300 text-center bg-white"
                data-testid={`pr-row-${it.date}`}
              >
                <div className="py-3 text-slate-900 font-heading font-black text-base border-r border-slate-300">
                  Shillong
                </div>
                <div className="py-3 text-slate-900 font-heading font-black text-base border-r border-slate-300">
                  {formatDMY(it.date)}
                </div>
                <div className="py-3 text-slate-900 font-heading font-black text-base border-r border-slate-300">
                  {it.placeholder || !it.r1 ? "XX" : String(it.r1).padStart(2, "0")}
                </div>
                <div className="py-3 text-slate-900 font-heading font-black text-base">
                  {it.placeholder || !it.r2 ? "XX" : String(it.r2).padStart(2, "0")}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <PageSeoNote topic="previous-results" />
    </>
  );
}
